import { useState } from "react";
import { Ellipsis, Pencil, Power, PowerOff, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { buttonVariants } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ACL_RELATIONSHIPS from "acl-relationships";
import { checkIsAuthorized } from "components/helpers";
import ConfirmDeleteDialog from "components/modals/ConfirmDeleteDialog";
import FormDialog from "components/modals/FormDialog";
import { deleteProduct, invalidateProductsQuery } from "hooks/useProductsTable";
import ProductForm from "routes/product/ProductForm";
import ProductService from "services/Product";

export function ProductsTableActions({ original }) {
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const canEdit = checkIsAuthorized([ACL_RELATIONSHIPS.product.edit]);
  const canDelete = checkIsAuthorized([ACL_RELATIONSHIPS.product.delete]);

  const handleToggleEnabled = async () => {
    await ProductService.update(original.id, {
      name: original.name,
      list_price: parseFloat(original.list_price),
      versions: original.versions,
      enabled: !original.enabled,
    });
    invalidateProductsQuery();
  };

  const handleDelete = async () => {
    try {
      await deleteProduct(original.id);
      toast.success("Product deleted");
      setDeleteOpen(false);
    } catch {
      toast.error("Failed to delete product");
    }
  };

  if (!canEdit && !canDelete) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label="Product actions"
          className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
        >
          <Ellipsis />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canEdit && (
            <DropdownMenuItem onClick={handleToggleEnabled}>
              {original.enabled ? <PowerOff /> : <Power />}
              {original.enabled ? "Disable" : "Enable"}
            </DropdownMenuItem>
          )}
          {canEdit && (
            <DropdownMenuItem onClick={() => setEditOpen(true)}>
              <Pencil />
              Edit
            </DropdownMenuItem>
          )}
          {canDelete && (
            <DropdownMenuItem
              variant="destructive"
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <FormDialog
        FormComponent={ProductForm}
        model={original}
        modelType="Product"
        open={editOpen}
        onOpenChange={setEditOpen}
        onSuccess={invalidateProductsQuery}
      />
      <ConfirmDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete product?"
        description="Are you sure you want to delete this Product?"
        onConfirm={handleDelete}
      />
    </>
  );
}
